import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

// ── Platform definitions ──────────────────────────────────────────────────────

const PLATFORMS = {
  facebook: {
    label: 'Facebook',
    icon: '📘',
    fields: [
      { key: 'appId', label: 'App ID' },
      { key: 'appSecret', label: 'App Secret', type: 'password' },
      { key: 'pageId', label: 'Page ID' },
      { key: 'pageAccessToken', label: 'Page Access Token', type: 'password' },
    ],
  },
  instagram: {
    label: 'Instagram',
    icon: '📸',
    fields: [
      { key: 'igUserId', label: 'IG User ID' },
      { key: 'accessToken', label: 'Access Token (linked via FB)', type: 'password' },
    ],
  },
  x: {
    label: 'X (Twitter)',
    icon: '🐦',
    fields: [
      { key: 'apiKey', label: 'API Key' },
      { key: 'apiSecret', label: 'API Secret', type: 'password' },
      { key: 'accessToken', label: 'Access Token' },
      { key: 'accessTokenSecret', label: 'Access Token Secret', type: 'password' },
    ],
  },
  linkedin: {
    label: 'LinkedIn',
    icon: '💼',
    fields: [
      { key: 'clientId', label: 'Client ID' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
      { key: 'accessToken', label: 'Access Token', type: 'password' },
    ],
  },
};

// ── Image generator definitions ───────────────────────────────────────────────

const IMAGE_PROVIDERS = {
  'dalle3': { label: 'DALL-E 3', icon: '🎨' },
  'sdxl': { label: 'Stability AI (SDXL)', icon: '🌟' },
  'flux': { label: 'Flux', icon: '⚡' },
};

const IMAGE_STYLES = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'vibrant', label: 'Vibrant' },
  { value: 'muted', label: 'Muted' },
];

const IMAGE_SIZES = [
  { value: '1024x1024', label: '1024×1024 (Square)' },
  { value: '1080x1080', label: '1080×1080 (Instagram)' },
  { value: '1080x1920', label: '1080×1920 (Story)' },
];

// ── Content preference options ────────────────────────────────────────────────

const TOPIC_PRESETS = [
  'Industry Tips', 'Company News', 'Behind-the-Scenes', 'Promotions', 'Educational',
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'humorous', label: 'Humorous' },
  { value: 'educational', label: 'Educational' },
  { value: 'inspirational', label: 'Inspirational' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: '3x/week', label: '3× per Week' },
  { value: 'weekly', label: 'Weekly' },
];

const POSTING_TIMES = ['9am', '12pm', '5pm'];

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

// ── Helper ─────────────────────────────────────────────────────────────────────

const apiCall = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `Request failed (${res.status})`);
  return data;
};

// ── Main component ────────────────────────────────────────────────────────────

export default function SocialSetup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  // Step 1 — social platforms
  const [connectedPlatforms, setConnectedPlatforms] = useState({});
  const [connectingPlatform, setConnectingPlatform] = useState(null); // platform key or null
  const [platformForms, setPlatformForms] = useState({});
  const [savingPlatform, setSavingPlatform] = useState(false);

  // Step 2 — image generator
  const [imageProviders, setImageProviders] = useState({}); // { dalle3: { connected, apiKey } }
  const [imageKeys, setImageKeys] = useState({ dalle3: '', sdxl: '', flux: '' });
  const [testingProvider, setTestingProvider] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageStyle, setImageStyle] = useState('photorealistic');
  const [imageSize, setImageSize] = useState('1024x1024');

  // Step 3 — content preferences
  const [topics, setTopics] = useState([]);
  const [topicInput, setTopicInput] = useState('');
  const [tone, setTone] = useState('professional');
  const [frequency, setFrequency] = useState('daily');
  const [postingTimes, setPostingTimes] = useState(['9am']);
  const [postingDays, setPostingDays] = useState(['mon', 'wed', 'fri']);
  const [autoPost, setAutoPost] = useState(false);
  const [cta, setCta] = useState('');
  const [autoPauseThreshold, setAutoPauseThreshold] = useState(20);

  // Step 4 — cron status
  const [cronStatus, setCronStatus] = useState('inactive'); // active | paused | inactive
  const [activating, setActivating] = useState(false);

  // ── Load setup status on mount ──────────────────────────────────────────────

  useEffect(() => {
    loadSetupStatus();
    loadCronStatus();
  }, []);

  const loadSetupStatus = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/social/setup-status');
      // API returns connectedPlatforms as array [{platform: 'x'}, ...] — convert to object map
      if (data.connectedPlatforms) {
        const map = {};
        for (const p of data.connectedPlatforms) {
          map[p.platform] = true;
        }
        setConnectedPlatforms(map);
      }
      // API returns imageConfig as {provider, hasApiKey, style, size}
      if (data.imageConfig) {
        const ic = data.imageConfig;
        const providers = {};
        if (ic.provider && ic.hasApiKey) {
          providers[ic.provider] = { connected: true, apiKey: '' };
        }
        setImageProviders(providers);
        if (ic.style) setImageStyle(ic.style);
        if (ic.size) setImageSize(ic.size);
      }
      if (data.autoPost !== undefined) setAutoPost(data.autoPost);
      if (data.cronActive) setCronStatus(data.cronActive ? 'active' : 'inactive');
    } catch (e) {
      console.error('Failed to load setup status:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadCronStatus = async () => {
    try {
      const data = await apiCall('/api/social/cron-status');
      setCronStatus(data.status || 'inactive');
    } catch (e) {
      // Endpoint may not exist yet — default to inactive
    }
  };

  // ── Computed counts ──────────────────────────────────────────────────────────

  const connectedPlatformCount = Object.values(connectedPlatforms).filter(Boolean).length;
  const connectedImageCount = Object.values(imageProviders).filter(p => p?.connected).length;

  // ── Step 1 handlers ────────────────────────────────────────────────────────

  const startConnect = (platformKey) => {
    setConnectingPlatform(platformKey);
    const existing = platformForms[platformKey] || {};
    const form = {};
    for (const f of PLATFORMS[platformKey].fields) {
      form[f.key] = existing[f.key] || '';
    }
    setPlatformForms({ ...platformForms, [platformKey]: form });
  };

  const cancelConnect = () => {
    setConnectingPlatform(null);
  };

  const handlePlatformFieldChange = (platformKey, fieldKey, value) => {
    setPlatformForms({
      ...platformForms,
      [platformKey]: { ...platformForms[platformKey], [fieldKey]: value },
    });
  };

  const savePlatformConnection = async (platformKey) => {
    setSavingPlatform(true);
    try {
      const creds = platformForms[platformKey];
      // Validate all fields filled
      for (const f of PLATFORMS[platformKey].fields) {
        if (!creds[f.key]?.trim()) {
          toast.error(`${f.label} is required`);
          setSavingPlatform(false);
          return;
        }
      }
      await apiCall(`/api/social/connect/${platformKey}`, {
        method: 'POST',
        body: JSON.stringify(creds),
      });
      toast.success(`${PLATFORMS[platformKey].label} connected successfully`);
      setConnectedPlatforms({ ...connectedPlatforms, [platformKey]: true });
      setConnectingPlatform(null);
    } catch (e) {
      toast.error(e.message || 'Failed to connect platform');
    } finally {
      setSavingPlatform(false);
    }
  };

  const disconnectPlatform = async (platformKey) => {
    if (!window.confirm(`Disconnect ${PLATFORMS[platformKey].label}?`)) return;
    try {
      await apiCall(`/api/social/disconnect/${platformKey}`, { method: 'POST' });
      toast.success(`${PLATFORMS[platformKey].label} disconnected`);
      setConnectedPlatforms({ ...connectedPlatforms, [platformKey]: false });
    } catch (e) {
      toast.error(e.message || 'Failed to disconnect');
    }
  };

  // ── Step 2 handlers ────────────────────────────────────────────────────────

  const handleImageKeyChange = (provider, value) => {
    setImageKeys({ ...imageKeys, [provider]: value });
  };

  const testImageConnection = async (provider) => {
    const apiKey = imageKeys[provider]?.trim();
    if (!apiKey) {
      toast.error('Please enter an API key first');
      return;
    }
    setTestingProvider(provider);
    setPreviewImage(null);
    try {
      const data = await apiCall('/api/social/test-image', {
        method: 'POST',
        body: JSON.stringify({
          provider,
          apiKey,
          style: imageStyle,
          size: imageSize,
        }),
      });
      if (data.imageUrl) {
        setPreviewImage(data.imageUrl);
        toast.success(`${IMAGE_PROVIDERS[provider].label} connection successful`);
        setImageProviders({
          ...imageProviders,
          [provider]: { connected: true, apiKey },
        });
      } else {
        toast.success(`${IMAGE_PROVIDERS[provider].label} connection successful`);
        setImageProviders({
          ...imageProviders,
          [provider]: { connected: true, apiKey },
        });
      }
    } catch (e) {
      toast.error(e.message || 'Connection test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  // ── Step 3 handlers ────────────────────────────────────────────────────────

  const addTopic = (topic) => {
    const t = topic.trim();
    if (!t) return;
    if (topics.find(x => x.toLowerCase() === t.toLowerCase())) return;
    setTopics([...topics, t]);
    setTopicInput('');
  };

  const removeTopic = (topic) => {
    setTopics(topics.filter(t => t !== topic));
  };

  const handleTopicInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTopic(topicInput);
    }
  };

  const togglePostingTime = (time) => {
    setPostingTimes(
      postingTimes.includes(time)
        ? postingTimes.filter(t => t !== time)
        : [...postingTimes, time]
    );
  };

  const togglePostingDay = (day) => {
    setPostingDays(
      postingDays.includes(day)
        ? postingDays.filter(d => d !== day)
        : [...postingDays, day]
    );
  };

  const savePreferences = async () => {
    try {
      await apiCall('/api/social/preferences', {
        method: 'POST',
        body: JSON.stringify({
          topics,
          tone,
          frequency,
          postingTimes,
          postingDays,
          autoPost,
          cta,
          autoPauseThreshold: Number(autoPauseThreshold),
          imageStyle,
          imageSize,
        }),
      });
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save preferences');
    }
  };

  // ── Step 4 handlers ────────────────────────────────────────────────────────

  const activateCron = async () => {
    if (connectedPlatformCount < 1 || connectedImageCount < 1) {
      toast.error('Connect at least 1 social platform and 1 image generator first');
      return;
    }
    setActivating(true);
    try {
      await apiCall('/api/social/activate-cron', { method: 'POST' });
      toast.success('Content generation activated');
      setCronStatus('active');
    } catch (e) {
      toast.error(e.message || 'Failed to activate');
    } finally {
      setActivating(false);
    }
  };

  const deactivateCron = async () => {
    setActivating(true);
    try {
      await apiCall('/api/social/deactivate-cron', { method: 'POST' });
      toast.success('Content generation deactivated');
      setCronStatus('inactive');
    } catch (e) {
      toast.error(e.message || 'Failed to deactivate');
    } finally {
      setActivating(false);
    }
  };

  // ── Step indicator ──────────────────────────────────────────────────────────

  const steps = [
    { num: 1, label: 'Connect Platforms' },
    { num: 2, label: 'Image Generator' },
    { num: 3, label: 'Content Preferences' },
    { num: 4, label: 'Activate' },
  ];

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((s, idx) => (
        <React.Fragment key={s.num}>
          <div className="flex items-center">
            <div
              className={`flex items-center justify-center h-10 w-10 rounded-full font-semibold text-sm transition-colors ${
                step === s.num
                  ? 'bg-blue-600 text-white'
                  : step > s.num
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-400'
              }`}
            >
              {step > s.num ? '✓' : s.num}
            </div>
            <span
              className={`ml-2 text-sm hidden sm:inline ${
                step >= s.num ? 'text-gray-100' : 'text-gray-500'
              }`}
            >
              {s.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`h-1 w-12 sm:w-16 mx-1 rounded-full transition-colors ${
                step > s.num ? 'bg-green-600' : 'bg-gray-700'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-lg text-gray-400">Loading setup…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-2">Social Media Management Setup</h1>
        <p className="text-sm text-gray-400 mb-6">
          Configure your social platforms, image generator, and content preferences in 4 steps.
        </p>

        <StepIndicator />

        {/* ───────── Step 1: Connect Social Platforms ───────── */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Step 1: Connect Social Platforms</h2>
            <p className="text-sm text-gray-400">Connect at least 1 platform to proceed to activation.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(PLATFORMS).map(([key, p]) => {
                const isConnected = connectedPlatforms[key];
                const isFormOpen = connectingPlatform === key;
                return (
                  <div key={key} className="bg-gray-800 rounded-lg p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{p.icon}</span>
                        <span className="font-medium">{p.label}</span>
                      </div>
                      <span
                        className={`text-xs rounded-full px-2 py-1 ${
                          isConnected
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-600 text-gray-300'
                        }`}
                      >
                        {isConnected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>

                    {isConnected ? (
                      <button
                        onClick={() => disconnectPlatform(key)}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
                      >
                        Disconnect
                      </button>
                    ) : isFormOpen ? (
                      <div className="space-y-3">
                        {p.fields.map((f) => (
                          <div key={f.key}>
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              {f.label}
                            </label>
                            <input
                              type={f.type || 'text'}
                              value={platformForms[key]?.[f.key] || ''}
                              onChange={(e) => handlePlatformFieldChange(key, f.key, e.target.value)}
                              className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                              placeholder={f.label}
                            />
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <button
                            onClick={() => savePlatformConnection(key)}
                            disabled={savingPlatform}
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
                          >
                            {savingPlatform ? 'Saving…' : 'Connect'}
                          </button>
                          <button
                            onClick={cancelConnect}
                            className="bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg px-4 py-2 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => startConnect(key)}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between pt-4">
              <div />
              <button
                onClick={() => setStep(2)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-2 font-medium"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ───────── Step 2: Image Generator ───────── */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Step 2: Connect Image Generator</h2>
            <p className="text-sm text-gray-400">Connect at least 1 image generator to proceed to activation.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(IMAGE_PROVIDERS).map(([key, p]) => {
                const isConnected = imageProviders[key]?.connected;
                const isTesting = testingProvider === key;
                return (
                  <div key={key} className="bg-gray-800 rounded-lg p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{p.icon}</span>
                        <span className="font-medium text-sm">{p.label}</span>
                      </div>
                      <span
                        className={`text-xs rounded-full px-2 py-1 ${
                          isConnected
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-600 text-gray-300'
                        }`}
                      >
                        {isConnected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <input
                      type="password"
                      value={imageKeys[key] || ''}
                      onChange={(e) => handleImageKeyChange(key, e.target.value)}
                      className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm mb-3 focus:border-blue-500 focus:outline-none"
                      placeholder="API Key"
                    />
                    <button
                      onClick={() => testImageConnection(key)}
                      disabled={isTesting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
                    >
                      {isTesting ? 'Testing…' : 'Test Connection'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Image style & size selectors */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image Style</label>
                <select
                  value={imageStyle}
                  onChange={(e) => setImageStyle(e.target.value)}
                  className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {IMAGE_STYLES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image Size</label>
                <select
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value)}
                  className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {IMAGE_SIZES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {previewImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Preview</label>
                  <img
                    src={previewImage}
                    alt="Generated preview"
                    className="rounded-lg max-h-64 border border-gray-600"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(1)}
                className="bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg px-6 py-2 font-medium"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-2 font-medium"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ───────── Step 3: Content Preferences ───────── */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Step 3: Content Preferences</h2>

            {/* Topics */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content Topics</label>
                <p className="text-xs text-gray-500 mb-2">Press Enter or comma to add. Quick presets below.</p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={topicInput}
                    onChange={(e) => setTopicInput(e.target.value)}
                    onKeyDown={handleTopicInputKeyDown}
                    className="flex-1 rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="e.g. Industry Tips"
                  />
                  <button
                    onClick={() => addTopic(topicInput)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
                {/* Presets */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {TOPIC_PRESETS.filter(p => !topics.find(t => t.toLowerCase() === p.toLowerCase())).map((p) => (
                    <button
                      key={p}
                      onClick={() => addTopic(p)}
                      className="bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-full px-3 py-1 text-xs"
                    >
                      + {p}
                    </button>
                  ))}
                </div>
                {/* Chips */}
                {topics.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {topics.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center bg-blue-900 text-blue-200 rounded-full px-3 py-1 text-xs"
                      >
                        {t}
                        <button
                          onClick={() => removeTopic(t)}
                          className="ml-1.5 text-blue-400 hover:text-blue-200"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {TONES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Posting Frequency</label>
                <div className="flex gap-2">
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setFrequency(f.value)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        frequency === f.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Posting times */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Posting Times</label>
                <div className="flex gap-2">
                  {POSTING_TIMES.map((t) => (
                    <button
                      key={t}
                      onClick={() => togglePostingTime(t)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        postingTimes.includes(t)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Posting days */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Posting Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((d) => (
                    <label
                      key={d.value}
                      className={`cursor-pointer rounded-lg px-3 py-2 text-sm font-medium border ${
                        postingDays.includes(d.value)
                          ? 'bg-blue-600 text-white border-blue-500'
                          : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={postingDays.includes(d.value)}
                        onChange={() => togglePostingDay(d.value)}
                        className="hidden"
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Auto-post toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-300">Auto-Post</label>
                  <p className="text-xs text-gray-500">
                    If ON, pre-approved style posts auto-publish. If OFF, all drafts need review.
                  </p>
                </div>
                <button
                  onClick={() => setAutoPost(!autoPost)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoPost ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoPost ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* CTA */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Call to Action</label>
                <input
                  type="text"
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  className="w-full rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Website URL, phone, or booking link"
                />
              </div>

              {/* Auto-pause threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Auto-Pause Threshold
                </label>
                <input
                  type="number"
                  min="0"
                  value={autoPauseThreshold}
                  onChange={(e) => setAutoPauseThreshold(e.target.value)}
                  className="w-32 rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum posts before auto-pausing content generation.
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(2)}
                className="bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg px-6 py-2 font-medium"
              >
                ← Back
              </button>
              <div className="flex gap-2">
                <button
                  onClick={savePreferences}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-2 font-medium"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ───────── Step 4: Activate ───────── */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Step 4: Activate Content Cron</h2>

            {/* Summary */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-lg space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 border-b border-gray-700 pb-2 mb-2">Summary</h3>

              <div>
                <span className="text-sm text-gray-400">Connected Platforms:</span>{' '}
                <span className="text-sm text-gray-100">
                  {Object.entries(connectedPlatforms)
                    .filter(([, v]) => v)
                    .map(([k]) => PLATFORMS[k].label)
                    .join(', ') || 'None'}
                </span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Image Generator:</span>{' '}
                <span className="text-sm text-gray-100">
                  {Object.entries(imageProviders)
                    .filter(([, v]) => v?.connected)
                    .map(([k]) => IMAGE_PROVIDERS[k].label)
                    .join(', ') || 'None'}
                </span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Image Style:</span>{' '}
                <span className="text-sm text-gray-100 capitalize">{imageStyle}</span>
                <span className="text-gray-600"> · </span>
                <span className="text-sm text-gray-400">Size:</span>{' '}
                <span className="text-sm text-gray-100">{imageSize}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Topics:</span>{' '}
                <span className="text-sm text-gray-100">{topics.join(', ') || 'None'}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Tone:</span>{' '}
                <span className="text-sm text-gray-100 capitalize">{tone}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Frequency:</span>{' '}
                <span className="text-sm text-gray-100">{frequency}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Posting Times:</span>{' '}
                <span className="text-sm text-gray-100">{postingTimes.join(', ') || 'None'}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Posting Days:</span>{' '}
                <span className="text-sm text-gray-100 capitalize">
                  {postingDays.map(d => DAYS_OF_WEEK.find(x => x.value === d)?.label).join(', ') || 'None'}
                </span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Auto-Post:</span>{' '}
                <span className={`text-sm font-medium ${autoPost ? 'text-green-400' : 'text-gray-400'}`}>
                  {autoPost ? 'ON' : 'OFF'}
                </span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Call to Action:</span>{' '}
                <span className="text-sm text-gray-100">{cta || 'None'}</span>
              </div>

              <div>
                <span className="text-sm text-gray-400">Auto-Pause Threshold:</span>{' '}
                <span className="text-sm text-gray-100">{autoPauseThreshold}</span>
              </div>
            </div>

            {/* Guard check */}
            {(connectedPlatformCount < 1 || connectedImageCount < 1) && (
              <div className="bg-yellow-900/40 border border-yellow-700 rounded-lg p-4 text-yellow-200 text-sm">
                ⚠️ You must connect at least 1 social platform and 1 image generator before activating.
                <div className="mt-2 text-xs text-yellow-400">
                  Connected platforms: {connectedPlatformCount} · Image generators: {connectedImageCount}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setStep(1)}
                    className="bg-yellow-700 hover:bg-yellow-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium"
                  >
                    Go to Platforms
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    className="bg-yellow-700 hover:bg-yellow-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium"
                  >
                    Go to Image Generator
                  </button>
                </div>
              </div>
            )}

            {/* Cron status & activate/deactivate */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-300">Content Generation Status:</span>
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 font-medium ${
                      cronStatus === 'active'
                        ? 'bg-green-500 text-white'
                        : cronStatus === 'paused'
                        ? 'bg-yellow-500 text-gray-900'
                        : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        cronStatus === 'active'
                          ? 'bg-white animate-pulse'
                          : cronStatus === 'paused'
                          ? 'bg-gray-700'
                          : 'bg-gray-400'
                      }`}
                    />
                    {cronStatus === 'active' ? 'Active' : cronStatus === 'paused' ? 'Paused' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={activateCron}
                  disabled={activating || connectedPlatformCount < 1 || connectedImageCount < 1}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {activating ? 'Processing…' : 'Activate Content Generation'}
                </button>
                <button
                  onClick={deactivateCron}
                  disabled={activating || cronStatus === 'inactive'}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Deactivate
                </button>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(3)}
                className="bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg px-6 py-2 font-medium"
              >
                ← Back
              </button>
              <div />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}